import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { getMe } from "../../api/hub/identity";
import { getCompanyRules, updateCompanyRules, rollbackCompanyRules, type CompanyRules } from "../../api/hub/open";
import "./platform-settings.css";

/** Keep the edit's base version fixed until saved or explicitly reloaded. */
export function CompanyRulesPage() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["hub", "me"], queryFn: getMe });
  const rules = useQuery({ queryKey: ["hub", "company-rules"], queryFn: getCompanyRules });
  const [edit, setEdit] = useState<{ version: number; content: string } | null>(null);
  const [notice, setNotice] = useState("");
  const canEdit = !!me.data && !me.data.isAI && !me.data.customerId && ["CEO", "CTO"].includes(me.data.role);
  const saved = (data: CompanyRules) => {
    qc.setQueryData(["hub", "company-rules"], data);
    setEdit(null);
    setNotice(`已保存第 ${data.version} 版，所有代理会收到更新。`);
  };
  const save = useMutation({ mutationFn: () => updateCompanyRules(edit!.version, edit!.content.trim()), onSuccess: saved });
  const rollback = useMutation({ mutationFn: (version: number) => rollbackCompanyRules(rules.data!.version, version), onSuccess: saved });
  const busy = save.isPending || rollback.isPending;
  const error = save.error || rollback.error;
  return <section className="work-page settings-platform company-rules">
    <header className="work-heading"><div><p className="work-eyebrow">中台设置</p><h1>公司规矩</h1></div><Link to="/settings/platform">返回设置</Link></header>
    <p>统一约定状态、完成证据、必须人做的事和命名。代理开工时先读公司规矩和自己的变化摘要。</p>
    {(rules.isPending || me.isPending) && <p role="status">正在加载公司规矩…</p>}
    {(rules.isError || me.isError) && <div role="alert"><p>加载失败：{rules.error?.message || me.error?.message}</p><button onClick={() => { void rules.refetch(); void me.refetch(); }}>重试</button></div>}
    {rules.data && !me.isError && !me.isPending && <>
      <p>当前第 {rules.data.version} 版 · {canEdit ? "你可以编辑或回退" : "仅 CEO 和 CTO 可以编辑，当前为只读"}</p>
      {edit && canEdit ? <form className="work-form" onSubmit={e => { e.preventDefault(); if (canEdit && edit.content.trim() && !busy) save.mutate(); }}>
        <label>公司规矩正文<textarea rows={16} value={edit.content} disabled={busy} onChange={e => setEdit({ ...edit, content: e.target.value })} /></label>
        <p className="work-meta">基于第 {edit.version} 版编辑；若已有新版本，保存会被拒绝，你的文字会保留。</p>
        <div className="work-toolbar"><button className="work-primary" disabled={busy || !edit.content.trim()}>保存新版本</button><button type="button" disabled={busy} onClick={() => setEdit(null)}>取消编辑</button></div>
      </form> : <><div className="company-rules-content">{rules.data.content || "还没有公司规矩，等待 CEO 或 CTO 确认第一版。"}</div>{canEdit && <button onClick={() => { save.reset(); rollback.reset(); setEdit({ version: rules.data.version, content: rules.data.content }); }}>编辑公司规矩</button>}</>}
      <h2>版本记录</h2>
      {!rules.data.versions.length && <p>尚无版本。</p>}
      <ul>{rules.data.versions.map(v => <li key={v.version}>第 {v.version} 版{v.rollbackOf != null ? `（由第 ${v.rollbackOf} 版回退）` : ""} · {new Date(v.createdAt).toLocaleString("zh-CN")}{canEdit && v.version !== rules.data.version && <button disabled={busy || !!edit} onClick={() => rollback.mutate(v.version)}>回退到第 {v.version} 版</button>}</li>)}</ul>
    </>}
    {error && <div role="alert"><p>{error.message}</p><p>如版本冲突，请保留编辑文字，取消编辑后重新加载最新版本再修改。</p><button disabled={busy} onClick={() => void rules.refetch()}>加载最新版本</button></div>}
    {notice && <p role="status">{notice}</p>}
  </section>;
}
